import { RepositoryRolePermissions } from './RepositoryRolePermissions';

export const RepositoryRoles = {
  namespaced: true,

  modules: {
    permissions: RepositoryRolePermissions,
  },

  state: {
    canCreate: false,
    loading: false,
    abilities: {},
    saving: false,
    sendingEmail: false,
    scopes: {},
    roles: {},
    rolesMeta: {},
  },

  getters: {
    areRolesLoaded: (state) => (uuid) => Object.keys(state.roles).includes(uuid),

    getScopeLabel: (state) => (scope) => state.scopes?.[scope] ?? null,

    getRoles: (state) => (uuid) => state.roles[uuid] ?? [],

    isUserAllowedToCreate: ({ canCreate }) => canCreate,

    areRolesLoading: (state) => state.loading,

    sendingEmail: (state) => state.sendingEmail,

    getRolesMeta: (state) => state.rolesMeta,
  },

  mutations: {
    updateRoles(state, newRoles) {
      state.roles = { ...state.roles, ...newRoles };
    },

    setRolesAbilities(state, abilities) {
      state.abilities = { ...abilities };
    },

    setCanCreate(state, allowed) {
      state.canCreate = allowed;
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setScopes(state, scopes) {
      state.scopes = { ...scopes };
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setSendingEmail(state, sending) {
      state.sendingEmail = sending;
    },

    setRolesMeta(state, meta) {
      state.rolesMeta = meta;
    },
  },

  actions: {
    async loadRoles({ commit }, { repository, filter, page, perPage }) {
      if (!repository) {
        return;
      }

      let roles = {};
      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.roles.index', {
          version: '1.0',
          repository,
          filter,
          page,
          perPage,
        });

        roles = response.data.data;
        const scopes = response.data.scopes ?? null;
        const abilities = response.data.can ?? {};

        commit('setRolesMeta', response.data.meta);

        if (scopes) {
          commit('setScopes', scopes);
        }

        commit('setRolesAbilities', abilities);
        commit('setCanCreate', response.data.can?.add);
      } catch (err) {
        console.error(err);
      }

      commit('updateRoles', { [repository]: roles });

      commit('setLoading', false);
    },

    async createRole({ commit }, { repository, name, scope, comment, has_2fa }) {
      commit('setSaving', true);

      try {
        await this._vm.$api(
          'repositories.roles.store',
          {
            version: '1.0',
            repository,
          },
          { name, scope, comment, has_2fa }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    async updateRole({ commit }, { repository, name, scope, id, comment, has_2fa }) {
      commit('setSaving', true);

      try {
        await this._vm.$api(
          'repositories.roles.update',
          {
            version: '1.0',
            repository,
            role: id,
          },
          { name, scope, comment, has_2fa }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    updateRoles({ commit }, { uuid, roles }) {
      commit('updateRoles', { [uuid]: roles });
    },

    async deleteRole({ commit }, { repository, role, comment }) {
      commit('setSaving', true);

      try {
        await this._vm.$api(
          'repositories.roles.destroy',
          {
            version: '1.0',
            repository,
            role,
          },
          { comment }
        );
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    async resendEmail({ commit }, { repository, role }) {
      commit('setSendingEmail', true);

      try {
        await this._vm.$api('repositories.roles.resend-2fa-invitation', {
          version: '1.0',
          repository,
          role,
        });
      } catch (err) {
        console.error(err);
      }

      commit('setSendingEmail', false);
    },
  },
};
